import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box } from '@mui/material';
import { getBillingSmsUsage } from '../api/pricingApi';
import { fetchCustomers, fetchTenants } from '../api/authApi';
import { DataTable } from '../components/DataTable';
import { FilterBar, FilterSelect, FilterSelectOption } from '../components/FilterBar';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { TableToolbar } from '../components/TableToolbar';
import { Button } from '../components/ui/Button';

const SMS_STATUSES = ['open', 'invoiced', 'closed'];

function formatPeriod(start, end, emptyValue) {
  if (!start && !end) {
    return emptyValue;
  }
  return `${start || '?'} – ${end || '?'}`;
}

export function BillingSmsUsagePage() {
  const { t } = useTranslation(['pricing', 'common', 'statuses']);
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [status, setStatus] = useState('');

  const emptyValue = t('common:empty_value');
  const recordCount = rows.length;

  const load = useCallback(() => {
    setLoading(true);
    setError('');

    const params = { per_page: 100 };
    if (customerId) params.customer_id = customerId;
    if (tenantId) params.tenant_id = tenantId;
    if (status) params.status = status;

    getBillingSmsUsage(params)
      .then((response) => setRows(response.data || []))
      .catch(() => setError(t('pricing:sms_usage.load_failed')))
      .finally(() => setLoading(false));
  }, [customerId, tenantId, status, t]);

  useEffect(() => {
    load();
    fetchCustomers().then((r) => setCustomers(r.data || [])).catch(() => setCustomers([]));
    fetchTenants().then((r) => setTenants(r.data || [])).catch(() => setTenants([]));
  }, [load]);

  const columns = useMemo(
    () => [
      {
        id: 'tenant',
        label: t('pricing:sms_usage.columns.tenant'),
        render: (row) => row.tenant?.name || row.tenant_id || emptyValue,
      },
      {
        id: 'customer',
        label: t('pricing:sms_usage.columns.customer'),
        render: (row) => row.customer?.name || row.customer_id || emptyValue,
      },
      {
        id: 'period',
        label: t('pricing:sms_usage.columns.period'),
        render: (row) => formatPeriod(row.period_start, row.period_end, emptyValue),
      },
      {
        id: 'successful_sms',
        label: t('pricing:sms_usage.columns.successful_sms'),
        render: (row) => (
          row.successful_sms_count === 0 || row.successful_sms_count
            ? row.successful_sms_count
            : emptyValue
        ),
      },
      {
        id: 'billable_sms',
        label: t('pricing:sms_usage.columns.billable_sms'),
        render: (row) => (
          row.billable_sms_count === 0 || row.billable_sms_count
            ? row.billable_sms_count
            : emptyValue
        ),
      },
      {
        id: 'unit_price',
        label: t('pricing:sms_usage.columns.unit_price'),
        render: (row) => t('pricing:currency_amount', {
          amount: row.unit_price_snapshot,
          currency: row.currency || 'EUR',
        }),
      },
      {
        id: 'amount',
        label: t('pricing:sms_usage.columns.amount'),
        render: (row) => t('pricing:currency_amount', {
          amount: row.amount,
          currency: row.currency || 'EUR',
        }),
      },
      {
        id: 'status',
        label: t('common:status'),
        render: (row) => (
          row.status
            ? <StatusBadge status={row.status} category="invoice" size="Sm" />
            : emptyValue
        ),
      },
    ],
    [emptyValue, t],
  );

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('pricing:sms_usage.title')}
        subtitle={t('pricing:sms_usage.subtitle')}
        actions={(
          <Button variant="Secondary" component={RouterLink} to="/billing">
            {t('common:dashboard')}
          </Button>
        )}
      />

      <FilterBar type="Simple">
        <FilterSelect
          label={t('pricing:sms_usage.columns.customer')}
          hideLabel
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          minWidth={180}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {customers.map((customer) => (
            <FilterSelectOption key={customer.id} value={String(customer.id)}>
              {customer.name}
            </FilterSelectOption>
          ))}
        </FilterSelect>
        <FilterSelect
          label={t('pricing:sms_usage.columns.tenant')}
          hideLabel
          value={tenantId}
          onChange={(e) => setTenantId(e.target.value)}
          minWidth={180}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {tenants.map((tenant) => (
            <FilterSelectOption key={tenant.id} value={String(tenant.id)}>
              {tenant.name}
            </FilterSelectOption>
          ))}
        </FilterSelect>
        <FilterSelect
          label={t('pricing:sms_usage.status_filter')}
          hideLabel
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          minWidth={140}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {SMS_STATUSES.map((value) => (
            <FilterSelectOption key={value} value={value}>
              {value}
            </FilterSelectOption>
          ))}
        </FilterSelect>
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
        <TableToolbar
          mode="Default"
          title={t('pricing:sms_usage.title')}
          meta={(
            <StatusBadge
              status="list"
              category="generic"
              apiLabel={t('pricing:sms_usage.toolbar_count', { count: recordCount })}
              size="Sm"
              tone="Neutral"
            />
          )}
        />

        {loading ? (
          <LoadingSkeleton
            type="Table"
            embedded
            rows={3}
            columns={columns.length}
          />
        ) : (
          <DataTable
            type="Default"
            embedded
            columns={columns}
            rows={rows}
            getRowKey={(row) => row.id}
            emptyMessage={t('pricing:sms_usage.no_items')}
            sx={{
              overflowX: 'auto',
              width: '100%',
              '& table': {
                width: '100%',
                minWidth: '100%',
              },
            }}
          />
        )}
      </SectionCard>
    </Box>
  );
}
